import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Navigation } from "../components/site/Navigation";
import { Footer } from "../components/site/Footer";
import { CartDrawer } from "../components/site/CartDrawer";
import {
  ArrowLeft,
  ShoppingBag,
  Minus,
  Plus,
  MessageCircle,
  Mail,
  Loader2,
} from "lucide-react";
import {
  openWhatsAppOrder,
  openEmailOrder,
  ORDER_WHATSAPP_DISPLAY,
  ORDER_EMAIL,
} from "../lib/order";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/files/") || u.startsWith("/uploads/"))
    return `${BACKEND}${u}`;
  return u;
};

const money = (n) =>
  Number(n).toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add, setOpen: openCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then(({ data }) => {
        if (alive) setProduct(data);
      })
      .catch(() => {
        if (alive) setNotFound(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  const onAdd = () => {
    if (!product) return;
    add(product, qty);
    toast.success(`${qty} × ${product.name} adăugat în coș`);
  };

  const onAddAndOpen = () => {
    onAdd();
    openCart(true);
  };

  const onQuickOrder = (channel) => {
    if (!product) return;
    const items = [{ ...product, qty }];
    const total = product.price != null ? product.price * qty : 0;
    if (channel === "wa") openWhatsAppOrder(items, total);
    else openEmailOrder(items, total);
  };

  return (
    <div
      className="bg-[#080706] text-[#FDF8F5] min-h-screen"
      data-testid="product-detail-page"
    >
      <Navigation />
      <CartDrawer />

      <main className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <button
            onClick={() => navigate(-1)}
            data-testid="product-back-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#9D8B7B] hover:text-[#C8A97E] transition-colors"
          >
            <ArrowLeft size={14} /> Înapoi la catalog
          </button>

          {loading ? (
            <div className="py-32 flex items-center justify-center text-[#9D8B7B]">
              <Loader2 className="animate-spin mr-2" size={18} /> Se încarcă
              produsul...
            </div>
          ) : notFound || !product ? (
            <div className="py-32 text-center" data-testid="product-not-found">
              <p className="font-[Playfair_Display] text-3xl">
                Produsul nu <em className="italic text-[#C8A97E]">există.</em>
              </p>
              <Link
                to="/"
                className="inline-block mt-6 text-xs uppercase tracking-[0.22em] text-[#C8A97E] hover:text-[#FDF8F5]"
              >
                → Înapoi la site
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Image */}
              <div className="lg:col-span-6">
                <div
                  className="aspect-[4/5] bg-white border border-[#2A2522] overflow-hidden"
                  data-testid="product-image"
                >
                  {product.image ? (
                    <img
                      src={absUrl(product.image)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#FAF6EE] flex items-center justify-center">
                      <span className="font-[Playfair_Display] italic text-[#C8A97E]/60 text-9xl">
                        {product.category?.charAt(0) || "P"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="lg:col-span-6 lg:pl-6">
                <span
                  className="inline-block text-[10px] uppercase tracking-[0.28em] text-[#C8A97E] bg-[#0F0D0B] border border-[#C8A97E]/30 px-3 py-1"
                  data-testid="product-category"
                >
                  {product.category}
                </span>
                <h1
                  className="mt-6 font-[Playfair_Display] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                  data-testid="product-name"
                >
                  {product.name}
                </h1>

                {product.price != null && product.price !== "" && (
                  <p
                    className="mt-8 font-[Playfair_Display] text-4xl text-[#C8A97E]"
                    data-testid="product-price"
                  >
                    {money(product.price)}{" "}
                    <span className="text-lg text-[#9D8B7B]">lei</span>
                  </p>
                )}

                <div className="mt-8 grid grid-cols-2 gap-6 max-w-md border-t border-[#1F1A18] pt-6">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B5E55]">
                      Gramaj / Volum
                    </p>
                    <p className="text-[#FDF8F5] font-light mt-1">
                      {product.size || "—"}
                    </p>
                  </div>
                  {product.pack && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B5E55]">
                        Ambalaj
                      </p>
                      <p className="text-[#FDF8F5] font-light mt-1">
                        {product.pack}
                      </p>
                    </div>
                  )}
                </div>

                {product.description && (
                  <div className="mt-8 border-t border-[#1F1A18] pt-6">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C8A97E] mb-3">
                      — Descriere
                    </p>
                    <p
                      className="text-[#C8B9A8] font-light leading-relaxed whitespace-pre-line"
                      data-testid="product-description"
                    >
                      {product.description}
                    </p>
                  </div>
                )}

                {/* Qty + add to cart */}
                <div className="mt-10 flex items-center gap-4">
                  <div className="flex items-center border border-[#2A2522]">
                    <button
                      data-testid="product-qty-dec"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-11 h-12 flex items-center justify-center hover:bg-[#0F0D0B]"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-14 text-center text-sm" data-testid="product-qty">
                      {qty}
                    </span>
                    <button
                      data-testid="product-qty-inc"
                      onClick={() => setQty((q) => q + 1)}
                      className="w-11 h-12 flex items-center justify-center hover:bg-[#0F0D0B]"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    data-testid="product-add-to-cart"
                    onClick={onAddAndOpen}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C8A97E] text-[#080706] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#B3956D] transition-colors"
                  >
                    <ShoppingBag size={16} strokeWidth={1.6} />
                    Adaugă în coș
                  </button>
                </div>

                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#6B5E55]">
                  — Sau comandă direct doar acest produs:
                </p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    data-testid="product-quick-wa"
                    onClick={() => onQuickOrder("wa")}
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-[#0A2E17] px-6 py-3.5 text-xs font-medium tracking-wide hover:bg-[#20bd5c] transition-colors"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </button>
                  <button
                    data-testid="product-quick-email"
                    onClick={() => onQuickOrder("email")}
                    className="inline-flex items-center justify-center gap-2 border border-[#3D3530] text-[#FDF8F5] px-6 py-3.5 text-xs font-medium tracking-wide hover:border-[#C8A97E] transition-colors"
                  >
                    <Mail size={14} /> Email
                  </button>
                </div>

                <div className="mt-8 pt-6 border-t border-[#1F1A18] text-[10px] uppercase tracking-[0.2em] text-[#6B5E55] space-y-1">
                  <p>WhatsApp: {ORDER_WHATSAPP_DISPLAY}</p>
                  <p className="normal-case tracking-normal">Email: {ORDER_EMAIL}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
